
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { BarChart, PieChart, ArrowLeft } from 'lucide-react';
import { Bar, BarChart as RechartsBarChart, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

// Admin users emails - in a real app, this would be stored in a database with proper roles
const ADMIN_EMAILS = ['owner@example.com', 'admin@example.com'];

interface AggregateData {
  totalAssessments: number;
  averageNEV: number;
  averageEROI: number;
  industryBreakdown: { industry: string; count: number }[];
  nevDistribution: { range: string; count: number }[];
  eroiDistribution: { range: string; count: number }[];
  recentAssessments: any[];
}

const AdminDashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [aggregateData, setAggregateData] = useState<AggregateData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    
    // Check if user is authenticated and is an admin
    if (!user) {
      navigate('/auth');
      return;
    }
    
    // Check if user is an admin
    if (!ADMIN_EMAILS.includes(user.email || '')) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to access the admin dashboard.",
        variant: "destructive",
      });
      navigate('/');
      return;
    }
    
    fetchAggregateData();
  }, [user, loading, navigate]);

  const fetchAggregateData = async () => {
    setIsLoading(true);
    try {
      // Fetch all survey responses (as admin)
      const { data: surveyResponses, error } = await supabase
        .from('survey_responses')
        .select('*');

      if (error) throw error;

      if (!surveyResponses || surveyResponses.length === 0) {
        setAggregateData({
          totalAssessments: 0,
          averageNEV: 0,
          averageEROI: 0,
          industryBreakdown: [],
          nevDistribution: [],
          eroiDistribution: [],
          recentAssessments: []
        });
        setIsLoading(false);
        return;
      }

      // Calculate average NEV and EROI
      const totalNEV = surveyResponses.reduce((sum, response) => 
        sum + (response.score_breakdown?.totalNEV || 0), 0);
      const totalEROI = surveyResponses.reduce((sum, response) => 
        sum + (response.score_breakdown?.eroi || 0), 0);
      
      // Create industry breakdown
      const industries: Record<string, number> = {};
      surveyResponses.forEach(response => {
        const industry = response.industry;
        industries[industry] = (industries[industry] || 0) + 1;
      });
      
      // Create NEV distribution
      const nevRanges: Record<string, number> = {
        "0-10": 0, "11-20": 0, "21-30": 0, "31-40": 0, "41-50": 0
      };
      
      surveyResponses.forEach(response => {
        const nev = response.score_breakdown?.totalNEV || 0;
        if (nev <= 10) nevRanges["0-10"]++;
        else if (nev <= 20) nevRanges["11-20"]++;
        else if (nev <= 30) nevRanges["21-30"]++;
        else if (nev <= 40) nevRanges["31-40"]++;
        else nevRanges["41-50"]++;
      });
      
      // Create EROI distribution
      const eroiRanges: Record<string, number> = {
        "0-1": 0, "1-2": 0, "2-3": 0, "3-4": 0, "4+": 0
      };
      
      surveyResponses.forEach(response => {
        const eroi = response.score_breakdown?.eroi || 0;
        if (eroi <= 1) eroiRanges["0-1"]++;
        else if (eroi <= 2) eroiRanges["1-2"]++;
        else if (eroi <= 3) eroiRanges["2-3"]++;
        else if (eroi <= 4) eroiRanges["3-4"]++;
        else eroiRanges["4+"]++;
      });
      
      // Get most recent assessments
      const recentAssessments = [...surveyResponses]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 5);
      
      setAggregateData({
        totalAssessments: surveyResponses.length,
        averageNEV: totalNEV / surveyResponses.length,
        averageEROI: totalEROI / surveyResponses.length,
        industryBreakdown: Object.entries(industries).map(([industry, count]) => ({
          industry,
          count
        })),
        nevDistribution: Object.entries(nevRanges).map(([range, count]) => ({
          range,
          count
        })),
        eroiDistribution: Object.entries(eroiRanges).map(([range, count]) => ({
          range,
          count
        })),
        recentAssessments
      });
    } catch (error) {
      console.error('Error fetching aggregate data:', error);
      toast({
        title: "Error",
        description: "Failed to load aggregate data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Logo className="mx-auto mb-4" />
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="w-full flex justify-between items-center mb-2">
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
          </Button>
          <LanguageSwitcher />
        </div>
        
        <div className="mb-8 flex flex-col items-center">
          <Logo className="mb-4" />
          <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
          <p className="text-gray-500">Aggregate Assessment Data</p>
        </div>

        {aggregateData?.totalAssessments === 0 ? (
          <Card className="mb-8 text-center py-12">
            <CardContent>
              <p className="text-gray-500 mb-6">No assessment data available yet.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-500">Total Assessments</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{aggregateData?.totalAssessments}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-500">Average NEV Score</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{aggregateData?.averageNEV.toFixed(1)}/50</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-500">Average EROI</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{aggregateData?.averageEROI.toFixed(2)}x</p>
                </CardContent>
              </Card>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart className="mr-2 h-5 w-5" /> 
                    Industry Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsBarChart
                        data={aggregateData?.industryBreakdown || []}
                        margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                      >
                        <XAxis 
                          dataKey="industry" 
                          angle={-45}
                          textAnchor="end"
                          height={70}
                        />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#8884d8" />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <PieChart className="mr-2 h-5 w-5" /> 
                    NEV Score Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsBarChart
                        data={aggregateData?.nevDistribution || []}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <XAxis dataKey="range" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#82ca9d" />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Recent Assessments</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project Name</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>NEV Score</TableHead>
                      <TableHead>EROI</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aggregateData?.recentAssessments.map((assessment) => (
                      <TableRow key={assessment.id}>
                        <TableCell className="font-medium">{assessment.project_name}</TableCell>
                        <TableCell>{assessment.industry}</TableCell>
                        <TableCell>{assessment.score_breakdown.totalNEV.toFixed(1)}</TableCell>
                        <TableCell>{assessment.score_breakdown.eroi.toFixed(2)}x</TableCell>
                        <TableCell>{new Date(assessment.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
